import { actions, WatchlistsStore, WATCHLISTS_STORE_NAME } from './store';
import { frontendActions, generateApiStoreKey, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi, CmcWebApiCommon, CmcWebApiTypes, IWebApiRequestBody } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';
import { WATCHLIST_API_NAMES } from './apiNames';
import { getConfig } from '@cmctechnology/webinvest-store-frontend';
import { IWatchlistItemPortfolio } from './types';
import { WATCHLIST_ITEMS_MAX_PAGE_SIZE, WatchlistView } from './constants';

type ResourceKey = string | number;

type GetState = typeof WatchlistsStore.getState;

export const makeWatchlistItemResourceKey = (id: number, instrumentOrItemId: string | number) => `${id}_${instrumentOrItemId}`;

export const fetchWatchlistItems = (id: number, refresh: boolean = false) => {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const hash = refresh ? 0 : (getState()[WATCHLISTS_STORE_NAME].items?.[id]?.hash ?? 0);
    const request: CmcWebApi.Watchlists.WatchListRequest3 & IWebApiRequestBody = {
      ListId: id as number,
      Page: 0,
      PageSize: WATCHLIST_ITEMS_MAX_PAGE_SIZE,
      View: WatchlistView.Watchlist,
      _config: { hash }
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Watchlists.WatchListModel2>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListGet3,
        request,
        dispatch,
        id,
        refresh,
        true
      );
      dispatch(actions.watchlistItemsFetched({ id, response, hash: response._apiInfo?.Hash ?? 0 }));
    } catch {}
  };
};

export const onPollFetchWatchlistItems =
  (subscriptions: ResourceKey[] = []) =>
  (dispatch: Dispatch<any>) => {
    subscriptions.forEach((id) => {
      dispatch(fetchWatchlistItems(Number(id)));
    });
  };

export const subscribeWatchlistItems = (location: LocationType, id: number) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.subscribe({
      onPoll: onPollFetchWatchlistItems,
      location,
      apiName: WATCHLIST_API_NAMES.WatchListGet3,
      storeName: WATCHLISTS_STORE_NAME,
      resourceKeys: [id],
      pollTimeout: getConfig().pollTimeout
    })
  );
};

export const unsubscribeWatchlistItems = (location: LocationType, id: number) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.unsubscribe({
      location,
      apiName: WATCHLIST_API_NAMES.WatchListGet3,
      storeName: WATCHLISTS_STORE_NAME,
      resourceKeys: [id]
    })
  );
};

export const addWatchlistItem = (id: number, instrument: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListAddStockRequest1 & IWebApiRequestBody = {
      Id: id as number,
      SeedStock: instrument
    };
    const resourceKey = makeWatchlistItemResourceKey(id, instrument);

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1 & CmcWebApiTypes.Types.BadRequestModel>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListAddStock1,
        request,
        dispatch,
        resourceKey,
        true,
        false,
        false,
        true
      );
      if (response.Success) {
        dispatch(fetchWatchlistItems(id, true));
      }
      if (response.Errors) {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListAddStock1, resourceKey),
            errorCode: response.Errors.flatMap((e) => e.Errors).join(',')
          })
        );
      }
    } catch {}
  };
};

export const deleteWatchlistItem = (id: number, itemId: number) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListItemDeleteRequest1 & IWebApiRequestBody = {
      ListId: undefined!,
      ItemId: itemId as number,
      Stock: undefined!
    };
    const resourceKey = makeWatchlistItemResourceKey(id, itemId);

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListItemDelete1,
        request,
        dispatch,
        resourceKey,
        true,
        false,
        false,
        true
      );
      if (response.Success) {
        dispatch(actions.watchlistItemDeleted({ id, itemId }));
      } else {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListItemDelete1, resourceKey),
            errorCode: response.UiMessage
          })
        );
      }
    } catch {}
  };
};

/**
 * Backend handles deleting by item ID or combination of instrument and watchlist ID (legacy reasons).
 * Fetching items return each item with property WatchListItemId set to 0
 * @param id Watchlist ID
 * @param instrument Instrument Code
 */
export const deleteWatchlistItemByStockCode = (id: number, instrument: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListItemDeleteRequest1 & IWebApiRequestBody = {
      ListId: id as number,
      ItemId: undefined!,
      Stock: instrument
    };
    const resourceKey = makeWatchlistItemResourceKey(id, instrument);

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListItemDelete1,
        request,
        dispatch,
        resourceKey,
        true,
        false,
        false,
        true
      );
      if (response.Success) {
        dispatch(actions.watchlistItemForInstrumentDeleted({ id, instrument }));
      } else {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListItemDelete1, resourceKey),
            errorCode: response.UiMessage
          })
        );
      }
    } catch {}
  };
};

export const saveWatchlistItem = (id: number, itemId: number, item: IWatchlistItemPortfolio) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListItemSaveRequest1 & IWebApiRequestBody = {
      ListId: id as number,
      CurrentView: undefined!,
      Item: {
        WatchListItemId: itemId,
        PortfolioPurchaseDate: new Date(item.purchaseDate),
        PortfolioPurchasePrice: item.purchasePrice,
        PortfolioPurchaseQuantity: item.purchaseQuantity
      } as CmcWebApi.Watchlists.WatchListItemModel1
    };
    const resourceKey = makeWatchlistItemResourceKey(id, itemId);

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1 & CmcWebApiTypes.Types.BadRequestModel>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListItemSave1,
        request,
        dispatch,
        resourceKey,
        true,
        false,
        false,
        true
      );
      if (response.Success) {
        dispatch(actions.watchlistItemSaved({ id, itemId, item }));
      } else if (response.Errors) {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListItemSave1, resourceKey),
            errorCode: response.Errors.flatMap((e) => e.Errors).join(',')
          })
        );
      } else {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListItemSave1, resourceKey),
            errorCode: response.UiMessage
          })
        );
      }
    } catch {}
  };
};
